import { Alignment, EventType, Fit, Layout, useRive } from "@rive-app/react-canvas";
import styled, { css } from "styled-components";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const RIVE_FILE_PATH = "/beam_hero_animation.riv";
const STATE_MACHINE_NAME = "State Machine 1";
const CLIPBOARD_EVENTS = [
  "Clipboard 1 (FT) event click",
  "Clipboard 2 (ET) event click",
  "Clipboard 3 (CT) event click",
] as const;

const PYTHON_SNIPPETS = {
  [CLIPBOARD_EVENTS[0]]: `from beam import function

@function(gpu="H100", cpu=16, memory="32Gi")
def train():
    MyModel().fit("training_data.csv")
    print("✓ Model Trained!")

if __name__ == "__main__":
    train.remote()`,
  [CLIPBOARD_EVENTS[1]]: `from beam import endpoint, vllm

@endpoint(gpu="A100-40", keep_warm_seconds=60)
def predict():
    llm = vllm.LLM(model="facebook/opt-125m")
    return {"prediction": llm.generate("The future of AI is")[0].text}`,
  [CLIPBOARD_EVENTS[2]]: `from beam import Image, Pod

notebook = Pod(
    gpu="A100-80",
    image=Image(base_image="jupyter/base-notebook:latest"),
    entrypoint=["start-notebook.py"],
)`,
} as const;

const RiveWrapper = () => {
  const [currentAnimation, setCurrentAnimation] = useState(0);
  const [animations, setAnimations] = useState<{ name: string; stateMachineName: string }[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const riveContainerRef = useRef<HTMLDivElement>(null);

  const { rive, RiveComponent } = useRive({
    src: RIVE_FILE_PATH,
    stateMachines: STATE_MACHINE_NAME,
    autoplay: true,
    artboard: "Hero - Main artboard 2x",
    layout: new Layout({
      fit: Fit.Contain,
      alignment: Alignment.Center,
    }),
    onLoad: () => {
      rive?.resizeDrawingSurfaceToCanvas();
      setTimeout(() => setIsLoaded(true), 100);
    },
  });

  const resizeRiveCanvas = () => {
    if (!rive || !riveContainerRef.current) return;
    const container = riveContainerRef.current;

    const canvas = container.querySelector("canvas");
    if (!canvas) return;

    const pixelRatio = window.devicePixelRatio || 1;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${containerHeight}px`;
    canvas.width = Math.floor(containerWidth * pixelRatio);
    canvas.height = Math.floor(containerHeight * pixelRatio);

    // Scale the drawing context
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.scale(pixelRatio, pixelRatio);
    }

    // Tell Rive to resize
    rive.resizeDrawingSurfaceToCanvas();
    rive.startRendering();
  };

  const copyPythonCodeToClipboard = useCallback(async (clipboardEvent: typeof CLIPBOARD_EVENTS[number]) => {
    try {
      await navigator.clipboard.writeText(PYTHON_SNIPPETS[clipboardEvent]);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  }, []);

  useEffect(() => {
    if (rive) {
      const availableAnimations = [
        { name: "TAB-1", stateMachineName: STATE_MACHINE_NAME },
        { name: "TAB-2", stateMachineName: STATE_MACHINE_NAME },
        { name: "TAB-3", stateMachineName: STATE_MACHINE_NAME },
      ];
      rive.on(EventType.RiveEvent, event => {
        if (
          event.data &&
          typeof event.data === "object" &&
          "name" in event.data &&
          typeof event.data.name === "string"
        ) {
          const clipboardEvent = CLIPBOARD_EVENTS.find(
            e => e === (event.data as { name: string }).name.trim()
          );
          if (clipboardEvent) {
            copyPythonCodeToClipboard(clipboardEvent);
          }
        }
      });
      setAnimations(availableAnimations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rive]);

  // Play a specific animation
  const playAnimation = (index: number) => {
    if (!rive || index >= animations.length) return;
    try {
      const animation = animations[index];
      rive.play(animation.name);
      setCurrentAnimation(index);
    } catch (error) {
      console.error("Error playing animation:", error);
    }
  };

  const handleAnimationClick = () => {
    if (animations.length === 0) return;
    const nextIndex = (currentAnimation + 1) % animations.length;
    playAnimation(nextIndex);
  };

  return (
    <RiveContainer
      ref={riveContainerRef}
      onClick={handleAnimationClick}
      onTransitionEnd={resizeRiveCanvas}
      style={{ cursor: "pointer" }}
      $isLoaded={isLoaded}
    >
      <RiveComponent />
    </RiveContainer>
  );
};

export default RiveWrapper;

const RiveContainer = styled.div<{ $isLoaded: boolean }>`
  width: 100%;
  height: 100%;
  opacity: 0;
  transform: scale(0.8);
  transition: opacity 0.8s ease-out, transform 0.9s cubic-bezier(0.34, 1.56, 0.64, 1);
  border-radius: 24px;
  ${props =>
    props.$isLoaded &&
    css`
      opacity: 1;
      transform: scale(1);
    `}
`;
